import React, { useEffect, useState } from 'react';
import { api } from '../../../core/utils/apiClient';

interface ShareInfo {
  is_shared: boolean;
  share_token: string | null;
  shared_at: string | null;
  share_url: string | null;
  message_count_at_snapshot: number | null;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  conversationId: number | null;
}

/**
 * Side-drawer for managing a conversation's public-share link.
 *
 * Flow mirrors Notion / Claude.ai shared chats:
 * 1. Fetch current share state on open
 * 2. "Share publicly" → POST /chat/conversations/:id/share, copy URL
 * 3. "Revoke link" → DELETE /chat/conversations/:id/share
 *
 * The backend stores a snapshot at share time so future messages don't leak —
 * we surface that explicitly so the owner doesn't expect live-updating shares.
 */
export const ShareChatDrawer: React.FC<Props> = ({ isOpen, onClose, conversationId }) => {
  const [info, setInfo] = useState<ShareInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Fetch current share state whenever the drawer opens for a real conversation.
  useEffect(() => {
    if (!isOpen || conversationId == null) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get(`/api/v1/chat/conversations/${conversationId}/share`)
      .then(async (res) => {
        if (cancelled) return;
        if (!res.ok) {
          setError("Couldn't load share state.");
          setInfo(null);
        } else {
          setInfo((await res.json()) as ShareInfo);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setError("Couldn't load share state.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, conversationId]);

  const fullShareUrl =
    info?.share_token
      ? `${typeof window !== 'undefined' ? window.location.origin : ''}/shared/${info.share_token}`
      : null;

  const handleShare = async () => {
    if (conversationId == null) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.post(`/api/v1/chat/conversations/${conversationId}/share`, {});
      if (!res.ok) {
        const body = await res.json().catch(() => ({} as any));
        throw new Error(body?.detail || body?.message || 'Failed to create share link');
      }
      const next = (await res.json()) as ShareInfo;
      setInfo(next);
      // Auto-copy to clipboard so the admin can paste immediately.
      if (next.share_token) {
        const url = `${window.location.origin}/shared/${next.share_token}`;
        await navigator.clipboard.writeText(url).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2500);
        });
      }
    } catch (e: any) {
      setError(e?.message || 'Could not create share link.');
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async () => {
    if (conversationId == null) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.delete(`/api/v1/chat/conversations/${conversationId}/share`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({} as any));
        throw new Error(body?.detail || body?.message || 'Failed to revoke share link');
      }
      const next = (await res.json()) as ShareInfo;
      setInfo(next);
    } catch (e: any) {
      setError(e?.message || 'Could not revoke share link.');
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async () => {
    if (!fullShareUrl) return;
    try {
      await navigator.clipboard.writeText(fullShareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setError('Clipboard access denied.');
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-40 z-50 transition-opacity"
        onClick={() => !busy && onClose()}
      />
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-xl z-50 overflow-y-auto">
        <div className="h-full flex flex-col">
          <div
            className="px-6 py-5 border-b flex items-center justify-between flex-shrink-0"
            style={{ borderColor: '#E5E7EB' }}
          >
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-900">Share this chat</h2>
              <p className="text-sm mt-1 text-gray-500">
                Anyone with the link can view the snapshot below.
              </p>
            </div>
            <button
              onClick={() => !busy && onClose()}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all flex-shrink-0"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="flex-1 px-6 py-6 space-y-5">
            {conversationId == null && (
              <div className="text-sm text-gray-500">
                Save the conversation first by sending a message — then you'll be able to share it.
              </div>
            )}

            {loading && conversationId != null && (
              <div className="text-sm text-gray-500">Loading…</div>
            )}

            {error && (
              <div
                className="text-sm rounded-lg p-3"
                style={{ background: '#FEF2F2', color: '#B42318', border: '1px solid #FECACA' }}
              >
                {error}
              </div>
            )}

            {info && info.is_shared && fullShareUrl && (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">
                    Public link
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      readOnly
                      value={fullShareUrl}
                      onFocus={(e) => e.target.select()}
                      className="flex-1 px-3 py-2 rounded-lg border bg-gray-50 text-sm text-gray-800"
                      style={{ borderColor: '#E5E7EB' }}
                    />
                    <button
                      type="button"
                      onClick={handleCopy}
                      className="px-3 py-2 rounded-lg text-sm font-medium border transition-colors"
                      style={{
                        borderColor: '#E5E7EB',
                        background: copied ? '#D1FAE5' : '#fff',
                        color: copied ? '#065F46' : '#374151',
                      }}
                    >
                      {copied ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                </div>

                <div
                  className="text-xs text-gray-500 p-3 rounded-lg"
                  style={{ background: '#F9FAFB', border: '1px solid #E5E7EB' }}
                >
                  Snapshot taken {info.shared_at ? new Date(info.shared_at).toLocaleString() : 'recently'}
                  {info.message_count_at_snapshot !== null && (
                    <> · {info.message_count_at_snapshot} messages</>
                  )}
                  . Future messages stay private — re-share to publish the new state.
                </div>

                <button
                  type="button"
                  onClick={handleRevoke}
                  disabled={busy}
                  className="w-full px-4 py-2.5 rounded-lg text-sm font-medium border transition-colors disabled:opacity-60"
                  style={{ borderColor: '#FECACA', color: '#B42318', background: '#fff' }}
                >
                  {busy ? 'Working…' : 'Revoke public link'}
                </button>
              </>
            )}

            {info && !info.is_shared && conversationId != null && (
              <>
                <div className="text-sm text-gray-600">
                  This chat is private. Generate a public link to share a read-only copy of the
                  current messages. Anyone with the link will be able to view (but not edit) it.
                </div>
                <button
                  type="button"
                  onClick={handleShare}
                  disabled={busy}
                  className="w-full px-4 py-2.5 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-60"
                  style={{ background: '#0E81FF' }}
                >
                  {busy ? 'Creating link…' : 'Create public link'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
